import { isMobileOnly } from 'react-device-detect';
import { Box, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { HallMaintenanceTaskAssignment, HallMaintenanceTaskAssignmentStatus } from '../../domain/HallMaintenanceTaskAssignment';

interface Props {
    assignments: HallMaintenanceTaskAssignment[];
    canManage: boolean;
    onEdit: (assignment: HallMaintenanceTaskAssignment) => void;
    onDelete: (assignment: HallMaintenanceTaskAssignment) => void;
}

const useAssigneeName = () => {
    const { t } = useTranslation();
    return (assignment: HallMaintenanceTaskAssignment) => {
        if (assignment.assignee) {
            const { firstName, lastName } = assignment.assignee;
            return `${lastName} ${firstName}`.trim();
        }
        return assignment.customAssignee || t("someone");
    };
};

const useStatusLabel = () => {
    const { t } = useTranslation();
    return (status?: HallMaintenanceTaskAssignmentStatus) => {
        if (!status) {
            return t("no_data");
        }
        switch (status) {
            case HallMaintenanceTaskAssignmentStatus.NotStarted:
                return t("assignment_status_not_started", { defaultValue: "Not started" });
            case HallMaintenanceTaskAssignmentStatus.InProgress:
                return t("assignment_status_in_progress", { defaultValue: "In progress" });
            case HallMaintenanceTaskAssignmentStatus.Completed:
                return t("assignment_status_completed", { defaultValue: "Completed" });
            default:
                return status;
        }
    };
};

const useStatusStyles = () => {
    const theme = useTheme();
    return (status?: HallMaintenanceTaskAssignmentStatus) => {
        if (status === HallMaintenanceTaskAssignmentStatus.Completed) {
            return { opacity: 0.55 };
        }
        if (status === HallMaintenanceTaskAssignmentStatus.InProgress) {
            return {
                borderLeft: `4px solid ${theme.palette.success.main}`,
                backgroundColor: alpha(theme.palette.success.light, 0.2),
            };
        }
        return {};
    };
};

const DesktopTable = (props: Props) => {
    const { t } = useTranslation();
    const assigneeName = useAssigneeName();
    const statusLabel = useStatusLabel();
    const statusStyles = useStatusStyles();

    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t("assignee")}</TableCell>
                        <TableCell>{t("start_date")}</TableCell>
                        <TableCell>{t("end_date")}</TableCell>
                        <TableCell>{t("status")}</TableCell>
                        <TableCell>{t("result_summary")}</TableCell>
                        {props.canManage && <TableCell align="right">{t("actions")}</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.assignments.map((assignment) => (
                        <TableRow key={assignment.id} hover sx={statusStyles(assignment.status)}>
                            <TableCell>
                                <Typography variant="subtitle2">{assigneeName(assignment)}</Typography>
                            </TableCell>
                            <TableCell>{moment(assignment.startDate).format("L")}</TableCell>
                            <TableCell>{assignment.endDate ? moment(assignment.endDate).format("L") : t("no_data")}</TableCell>
                            <TableCell>{statusLabel(assignment.status)}</TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                                <Typography variant="body2" color={assignment.resultSummary ? 'text.primary' : 'text.secondary'} noWrap>
                                    {assignment.resultSummary || t("no_data")}
                                </Typography>
                            </TableCell>
                            {props.canManage && (
                                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                    <Tooltip title={t("edit")}>
                                        <IconButton onClick={() => props.onEdit(assignment)}>
                                            <EditIcon color="primary" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t("delete")}>
                                        <IconButton onClick={() => props.onDelete(assignment)}>
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                    {props.assignments.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={props.canManage ? 6 : 5}>
                                <Typography variant="body2" color="text.secondary">{t("no_data")}</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const MobileList = (props: Props) => {
    const { t } = useTranslation();
    const assigneeName = useAssigneeName();
    const statusLabel = useStatusLabel();
    const statusStyles = useStatusStyles();

    return (
        <Stack spacing={1.25}>
            {props.assignments.map((assignment) => (
                <Paper key={assignment.id} elevation={1} sx={{ p: 1.25, ...statusStyles(assignment.status) }}>
                    <Stack spacing={0.75}>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                            <Typography variant="h6">{assigneeName(assignment)}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">{t("start_date")}</Typography>
                                <Typography variant="body2">{moment(assignment.startDate).format("L")}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">{t("end_date")}</Typography>
                                <Typography variant="body2">{assignment.endDate ? moment(assignment.endDate).format("L") : t("no_data")}</Typography>
                            </Box>
                        </Stack>
                        <Box>
                            <Typography variant="caption" color="text.secondary">{t("status")}</Typography>
                            <Typography variant="body2">{statusLabel(assignment.status)}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">{t("result_summary")}</Typography>
                            <Typography variant="body2" color={assignment.resultSummary ? 'text.primary' : 'text.secondary'}>
                                {assignment.resultSummary || t("no_data")}
                            </Typography>
                        </Box>
                        {props.canManage && (
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <IconButton size="small" onClick={() => props.onEdit(assignment)}>
                                    <EditIcon fontSize="small" color="primary" />
                                </IconButton>
                                <IconButton size="small" onClick={() => props.onDelete(assignment)}>
                                    <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                            </Stack>
                        )}
                    </Stack>
                </Paper>
            ))}
            {props.assignments.length === 0 && (
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t("no_data")}</Typography>
                </Paper>
            )}
        </Stack>
    );
};

const HallMaintenanceAssignmentsTable = (props: Props) => {
    if (isMobileOnly) {
        return <MobileList {...props} />;
    }

    return <DesktopTable {...props} />;
};

export default HallMaintenanceAssignmentsTable;
